/* eslint-disable require-jsdoc */
import PVP from '../constant/pvp';
import {convertGRank} from '@/lib/base/ranking.js';
import i18n from '@/i18n/index.js';
import filters from '@/lib/base/filters.js';

const LOBBY_TTL_SECONDS = 14 * 24 * 60 * 60;

export const COLOR_MODES = {
  RANDOM: 'random', // 猜子
  ASSIGNED: 'assigned', // 指定
};
export const HANDICAP_TYPES = {
  EVEN: 'even', // 分先
  ADVANTAGE: 'advantage', // 讓先
  STONES: 'stones', // 讓子
};
export const SCHEDULED_MODES = {
  ANYTIME: 'anytime', // 隨時
  SCHEDULED: 'scheduled', // 指定時間
};
export const SCORE_RULES = PVP.SCORE_RULE;

/**
 * Default empty form state used by StudentPvpSettingModal.
 * All form fields the modal manages live here.
 * @returns {Object} default form state
 */
export function defaultForm() {
  return {
    title: '',
    subAcademyId: '',
    subAcademyId1: '',
    classId1: '',
    username1: '',
    subAcademyId2: '',
    classId2: '',
    username2: '',
    colorMode: COLOR_MODES.RANDOM, // 'random' (猜子) | 'assigned' (指定)
    blackPlayerUsername: '', // only used when colorMode === 'assigned'
    handicapType: HANDICAP_TYPES.EVEN, // 'even' (分先) | 'advantage' (讓先) | 'stones' (讓子)
    handicapStones: 2, // only used when handicapType === 'stones'
    scheduledMode: SCHEDULED_MODES.ANYTIME, // 'anytime' (隨時) | 'scheduled' (指定時間)
    scheduledDate: '', // 'YYYY-MM-DD', only used when scheduledMode === 'scheduled'
    scheduledTime: '', // 'HH:mm', only used when scheduledMode === 'scheduled'
    boardSize: 19,
    timeMinutes: 20,
    byoYomiTime: 10, // seconds
    byoYomiAmount: 0,
    scoreRule: SCORE_RULES.CHINESE, // 'chinese' (數子法) | 'japanese' (比目法)
  };
}

/**
 * Converts the modal's form state into the API payload shape.
 * Update HERE if the backend changes field names.
 * @param {Object} form form state from defaultForm/StudentPvpSettingModal
 * @returns {Object} API payload
 */
export function formToPayload(form) {
  const payload = {
    boardSize: form.boardSize,
    time: form.timeMinutes * 60, // API expects seconds
    byoYomiTime: form.byoYomiTime,
    byoYomiAmount: form.byoYomiAmount,
    handicap: 0,
    lobbyTimeToLive: Math.floor(Date.now() / 1000) + LOBBY_TTL_SECONDS,
    rule: form.rule,
    remark: form.title,
    scoreRule: form.scoreRule,
    whoCanStart: form.username1,
    username1: form.username1,
    username2: form.username2,
    subAcademyId1: form.subAcademyId1,
    subAcademyId2: form.subAcademyId2,
    classId1: form.classId1,
    classId2: form.classId2,
  };

  const isScheduled = form.scheduledMode === SCHEDULED_MODES.SCHEDULED;
  if (isScheduled) {
    if (!form.scheduledDate || !form.scheduledTime) {
      throw new Error(
        'Scheduled date and time must be provided for scheduled lobbies'
      );
    }
    payload.startTime = new Date(
      `${form.scheduledDate}T${form.scheduledTime}`
    ).getTime();

    payload.lobbyTimeToLive = payload.startTime / 1000 + LOBBY_TTL_SECONDS; // lobby TTL should be calculated from scheduled start time for scheduled lobbies
  }

  if (form.handicapType === HANDICAP_TYPES.ADVANTAGE) {
    payload.handicap = 1;
  } else if (form.handicapType === HANDICAP_TYPES.STONES) {
    payload.handicap = form.handicapStones;
  }

  if (form.colorMode === COLOR_MODES.ASSIGNED) {
    if (form.blackPlayerUsername === form.username1) {
      payload.user1Color = 'black';
    } else if (form.blackPlayerUsername === form.username2) {
      payload.user1Color = 'white';
    } else {
      throw new Error(
        'Black player username must match either player1 or player2 when color mode is assigned'
      );
    }
  }

  return payload;
}

/**
 * Converts a single API response item into the internal lobby model.
 * Update HERE if the backend changes field names.
 * @param {Object} item raw API lobby item
 * @returns {Object} internal lobby model
 */
export function responseToLobby(item) {
  let handicapType = HANDICAP_TYPES.EVEN;
  if (item.handicap === 1) {
    handicapType = HANDICAP_TYPES.ADVANTAGE;
  } else if (item.handicap > 1) {
    handicapType = HANDICAP_TYPES.STONES;
  }

  if (item.whiteRealName && item.blackRealName) {
    item = {
      ...item,
      username1: item.black,
      username2: item.white,
      realName1: item.blackRealName || item.black,
      realName2: item.whiteRealName || item.white,
      user1Rank: item.blackRank,
      user2Rank: item.whiteRank,
    };
  }

  if (item.game && item.blackUser && item.whiteUser) {
    item = {
      ...item,
      ...item.game,
      username1: item.blackUser.username,
      username2: item.whiteUser.username,
      realName1: item.blackUser.realName || item.blackUser.username,
      realName2: item.whiteUser.realName || item.whiteUser.username,
      user1Rank: item.blackUser.userRank,
      user2Rank: item.whiteUser.userRank,
    };
  }

  const colorMode = item.user1Color ? COLOR_MODES.ASSIGNED : COLOR_MODES.RANDOM;

  const getColorModeLabel = (username) => {
    if (item.black === username) {
      return i18n.t('執黑');
    }
    if (item.white === username) {
      return i18n.t('執白');
    }
    if (colorMode === COLOR_MODES.RANDOM) {
      return i18n.t('猜子');
    }
    if (
      (item.user1Color === 'black' && item.username1 === username) ||
      (item.user1Color === 'white' && item.username1 !== username)
    ) {
      return i18n.t('執黑');
    }
    return i18n.t('執白');
  };

  const getDisplayName = ({username, rank, realName}) => {
    const rankLabel = convertGRank(rank);
    return `${rankLabel} ${realName || username} ${getColorModeLabel(
      username
    )}`;
  };

  return {
    id: item.id,
    title: item.remark || '',
    scheduledTime: item.startTime != null ? item.startTime : null,
    // status: item.status,
    username1: item.username1 || item.black || '',
    subAcademyId1: item.subAcademyId1 || '',
    classId1: item.classId1 || '',
    realName1: item.realName1 || item.username1 || item.black,
    user1RankLabel: convertGRank(item.user1Rank),
    user1ColorLabel: getColorModeLabel(item.username1),
    user1DisplayName: getDisplayName({
      username: item.username1 || item.black,
      rank: item.user1Rank,
      realName: item.realName1,
    }),
    user1IsIn: item.user1IsIn ?? false,
    user1IsReady: item.user1IsReady ?? false,
    username2: item.username2 || item.white || '',
    subAcademyId2: item.subAcademyId2 || '',
    classId2: item.classId2 || '',
    realName2: item.realName2 || item.username2 || item.white,
    user2RankLabel: convertGRank(item.user2Rank),
    user2ColorLabel: getColorModeLabel(item.username2),
    user2DisplayName: getDisplayName({
      username: item.username2 || item.white,
      rank: item.user2Rank,
      realName: item.realName2,
    }),
    user2IsIn: item.user2IsIn ?? false,
    user2IsReady: item.user2IsReady ?? false,
    colorMode,
    blackPlayerUsername:
      item.user1Color === 'black' ? item.username1 : item.username2,
    handicapType,
    handicapStones: item.handicap || 0,
    boardSize: item.boardSize,
    timeMinutes: Math.round((item.time || 0) / 60),
    byoYomiTime: item.byoYomiTime || 0,
    byoYomiAmount: item.byoYomiAmount || 0,
    scoreRule: item.scoreRule || SCORE_RULES.CHINESE,
    subAcademyId: 'subAcademyId',
    gameId: item.gameId || null,
    winningWay: item.winningWay
      ? filters.winningWayText(
          item.winningWay,
          item.scoreRule,
          item.komi,
          item.boardSize
        )
      : null,
    createdTime: new Date(item.createdTime).getTime(),
  };
}

/**
 * Converts the internal lobby model back into form state for edit mode.
 * @param {Object} lobby internal lobby model from responseToLobby
 * @returns {Object} form state for StudentPvpSettingModal
 */
export function lobbyToForm(lobby) {
  const isScheduled = lobby.scheduledTime != null;
  const dt = isScheduled ? new Date(lobby.scheduledTime) : null;

  return {
    ...defaultForm(),
    // subAcademyId1, classId1, subAcademyId2, classId2 are intentionally
    // left as empty strings (from defaultForm): the API response does not return
    // class/branch info, and the modal's cascade dropdowns re-derive them at runtime.
    title: lobby.title,
    username1: lobby.username1,
    subAcademyId1: lobby.subAcademyId1,
    classId1: lobby.classId1,
    username2: lobby.username2,
    subAcademyId2: lobby.subAcademyId2,
    classId2: lobby.classId2,
    colorMode: lobby.colorMode,
    blackPlayerUsername: lobby.blackPlayerUsername,
    handicapType: lobby.handicapType,
    handicapStones: lobby.handicapStones,
    scheduledMode: isScheduled
      ? SCHEDULED_MODES.SCHEDULED
      : SCHEDULED_MODES.ANYTIME,
    scheduledDate: dt
      ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(dt.getDate()).padStart(2, '0')}`
      : '',
    scheduledTime: dt
      ? `${String(dt.getHours()).padStart(2, '0')}:${String(
          dt.getMinutes()
        ).padStart(2, '0')}`
      : '',
    boardSize: lobby.boardSize,
    timeMinutes: lobby.timeMinutes,
    byoYomiTime: lobby.byoYomiTime,
    byoYomiAmount: lobby.byoYomiAmount,
    scoreRule: lobby.scoreRule,
  };
}
